import React, { useState } from 'react';
import classNames from 'classnames';
import { BlockedContainer } from 'src/Containers';
import {
	RDCModal,
	RDCModalBody,
	RDCModalFooter,
	RDCModalHeader,
} from 'src/Containers/RDCModal';
import Button, { ButtonVariants } from '../Button';
import { Input } from '../Controls';
import { ReactComponent as AlertIcon } from 'src/Themes/assets/BigAlert.svg';
import { ReactComponent as TrashIcon } from 'src/Themes/assets/BigTrash.svg';
import './ConfirmationModal.scss';

interface IConfirmationModalProps extends React.HtmlHTMLAttributes<HTMLDivElement> {
	/** Флаг отображения */
	isShow?: boolean;
	/** Текст заголовка окна */
	title: string;
	/** Текст окна ( html ) */
	text?: string;
	/** Дочерние элементы */
	children?: React.ReactNode;
	/** Автоматически закрывать после подтверждения ( по умолчанию включено ) */
	autoClose?: boolean;
	/** Отрисовка линии над футером ( по умолчанию отключено ) */
	renderFooterLine?: boolean;
	/** Цвет окна */
	color?: 'red' | '';
	/** Иконка окна ( по умолчанию alert ) */
	icon?: 'alert' | 'trash';
	commentAskText?: string;
	/** Текст кнопки согласия  */
	confirmTextButton?: string;
	/** Цвет кнопки согласия  */
	confirmColorButton?: ButtonVariants;
	/** Текст кнопки отмены */
	cancelTextButton?: string;
	/** Просить комментарий */
	askComment?: boolean;
	/** Комментарий обязателен */
	requiredComment?: boolean;
	/** Вызывается при закрытии окна */
	onClose: () => void;
	/** Вызывается положительном ответе */
	onConfirm: (comment?: string) => void;
	/** Вызывается отрицательном ответе */
	onCancel?: (comment?: string) => void;
}

/** Модальное окно подтверждения действия */
const ConfirmationModal: React.FC<IConfirmationModalProps> = (props) => {
	const {
		isShow = true,
		onClose,
		confirmColorButton = 'green',
		title,
		text,
		children,
		color = '',
		icon = 'alert',
		renderFooterLine = false,
		confirmTextButton = 'Да',
		cancelTextButton = 'Отмена',
		commentAskText = '',
		autoClose = true,
		askComment = false,
		requiredComment = false,
		onConfirm,
		onCancel,
		className,
		...otherProps
	} = props;

	const [comment, setComment] = useState<string>('');

	const onConfirmHandler = () => {
		if (autoClose) {
			onClose();
		}
		onConfirm(comment);
	};

	const onCancelHandler = () => {
		if (onCancel) {
			onCancel(comment);
		}
		onClose();
	};

	if (!isShow) {
		return null;
	}

	const renderIcon = () => {
		if (!text && !children && askComment) {
			return null;
		} else if (icon === 'alert') {
			return <AlertIcon />;
		} else if (icon === 'trash') {
			return <TrashIcon width={120} />;
		} else {
			return null;
		}
	};

	const renderAskComment = () => {
		if (!askComment) {
			return null;
		}
		return (
			<div className="comment-block">
				{!!commentAskText && (
					<label htmlFor="askComment">{commentAskText} </label>
				)}
				<Input
					id="askComment"
					type="textarea"
					value={comment}
					placeholder="Оставить примечание"
					onChangeValue={(v) => {
						return setComment(v);
					}}
				/>
			</div>
		);
	};

	const renderContent = () => {
		if (children) {
			return <div className="body-large">{!!children && children}</div>;
		} else if (text) {
			return (
				<div
					className="body-large"
					dangerouslySetInnerHTML={{ __html: text }}
				/>
			);
		} else {
			return null;
		}
	};

	return (
		<BlockedContainer isFullScreen onRequestClose={onClose}>
			<RDCModal
				id="rdc_ask_confirm_modal"
				className={classNames(className, color)}
				{...otherProps}
			>
				<RDCModalHeader onClose={onClose}>{title}</RDCModalHeader>
				<RDCModalBody pTextCentered>
					{renderIcon()}
					{renderContent()}
					{renderAskComment()}
				</RDCModalBody>
				<RDCModalFooter renderFooterLine={renderFooterLine}>
					<Button outline onClick={onCancelHandler}>
						{cancelTextButton}
					</Button>
					<Button
						variant={confirmColorButton}
						onClick={onConfirmHandler}
						disable={askComment && requiredComment}
					>
						{confirmTextButton}
					</Button>
				</RDCModalFooter>
			</RDCModal>
		</BlockedContainer>
	);
};

export default ConfirmationModal;
